<template>
  <div>
    <NavBar class="nav">
      <template #left>
        <BackButton data-testid="back-button" @click="onBackClick" />
      </template>
      <template v-if="!loading" #center>{{ description }}</template>
      <template #right>
        <WatchListButton
          v-if="isWatchlistButtonEnabled"
          :on-watch-list="isOnWatchlist"
          :ticker="ticker"
          data-testid="watchlist-button"
          @click="onWatchlistClick"
        />
      </template>
    </NavBar>
    <Notification group="wrn-trade" />
    <ContextColorProvider context="trade">
      <TwoColumnsContainerLoader v-if="loading" />
      <div v-else-if="error" class="instrument-view__error-container">
        <ActionableHelperView
          class="instrument-view__error-container__error"
          :on-retry="load"
        />
      </div>
      <div
        v-else-if="instrumentMessage"
        class="instrument-view__error-container"
      >
        <HelperView
          icon="ic_alert"
          :title="instrumentMessage.title"
          :description="instrumentMessage.description"
          class="instrument-view__error-container__error"
          data-testid="helper-view-price"
        />
      </div>

      <InstrumentContent
        v-else
        data-testid="instrument-content"
        :latest-price-date="latestPriceDate"
        :latest-price="price"
        :balance-available="balanceAvailable"
        :status="tradeStatus"
        :best-offer-buy="bestBuyPrice"
        :best-offer-sell="bestSellPrice"
        :lot-size="lotSize"
        :position="position"
        :can-operate="canOperate"
        :instrument-about="instrumentAbout"
        :instrument-status="instrumentStatus"
        :instrument="instrument"
        :description="description"
        @refresh-instruments="refreshInstruments"
        @on-accept-risk-terms="handleAcceptRiskTerms"
      />
    </ContextColorProvider>
  </div>
</template>

<script lang="ts">
import { Component, ProvideReactive } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';

import { RouteMixin } from '@/modules/common/mixins/route';
import {
  ActionableHelperView,
  NavBar,
  HelperView,
  ContextColorProvider
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { percentageWithSign } from '@/modules/common/helpers/percentage';
import { WatchListButton } from '@/modules/trade/components/WatchListButton';
import { TwoColumnsContainerLoader } from '@/modules/common/components/TwoColumnsContainer';
import { Notification } from '@/modules/common/components/Notification';

import {
  IInstrumentAbout,
  InstrumentPriceMessage,
  getInstrumentDetailView,
  getInstrumentPosition,
  getInstrumentPrice,
  getInstrumentStatus,
  InstrumentStatus
} from '@/modules/trade/services/instruments';

import { TradeInstrumentPositionPrice } from '@/modules/trade/types/instrument';
import { FeatureFlags } from '@/types/models/FeatureFlags';

import { InstrumentContent } from './components/InstrumentContent';

import { SignalR, IInstrumentStatus } from '@/modules/trade/services/signalr';
import { getBalances, AccountStatus } from '@/modules/trade/services/account';
import { NumberFormatted, StringFormatted } from '@/modules/trade/types';
import { trackInstrumentViewLoad, trackWatchlistButtonClick } from './trackers';

const tradePositionsModule = namespace('tradeModule');

@Component({
  filters: { percentageWithSign },
  components: {
    ActionableHelperView,
    NavBar,
    BackButton,
    HelperView,
    TwoColumnsContainerLoader,
    WatchListButton,
    Notification,
    InstrumentContent,
    ContextColorProvider
  }
})
export default class TradeInstrumentView extends mixins(RouteMixin) {
  @tradePositionsModule.Action('fetchStatus')
  private fetchStatus!: Function;

  @tradePositionsModule.State('tradeStatus')
  readonly tradeStatus!: AccountStatus;

  private bestSellPrice = 0;
  private bestBuyPrice = 0;
  private lotSize = 1;
  private price: NumberFormatted = {
    value: 0,
    formatted: '-'
  };

  private position: TradeInstrumentPositionPrice | null = null;

  private balanceAvailable: NumberFormatted | null = null;
  private loading = true;
  private error = false;
  private latestPriceDate: StringFormatted | null = null;
  private isOnWatchlist!: boolean;
  private canOperate = false;
  private instrumentMessage: InstrumentPriceMessage | null = null;
  private instrumentAbout!: IInstrumentAbout | Object;
  private instrumentStatus: IInstrumentStatus | Object = {};
  instrument: InstrumentStatus | null = null;
  description = '';

  // O usuário consegue digitar um ticker minúsculo direto pela URL, então estamos convertendo o ticker
  // para ser todo maiúsculo, para prevenir errors com serviços utilizados(api) na view que são case sensitive
  @ProvideReactive('ticker')
  ticker = '';

  mounted() {
    this.ticker = this.$route.params.ticker.toLocaleUpperCase();
    this.load();
  }

  get isWatchlistButtonEnabled() {
    return !this.error && !this.loading;
  }

  private async load() {
    this.loading = true;
    this.error = false;

    await Promise.all([
      this.socketConnect(),
      this.fetchStatus(),
      this.fetchInstrumentStatus(),
      this.fetchBalance(),
      this.fetchPositions(),
      this.fetchPrice(),
      this.fetchInstrumentDetailView()
    ]);
    this.loading = false;
    if (!this.error) {
      trackInstrumentViewLoad(
        this.instrument!.symbol,
        this.description,
        this.instrument!.type.formatted
      );
    }
  }

  private async socketConnect() {
    try {
      await SignalR.startConnection();
      await SignalR.invokeWithSubscription('JoinGroup', [this.ticker]);
    } catch (error) {
      console.log(error);
    }
  }

  async destroyed() {
    await SignalR.stopConnection();
  }

  private async fetchPrice() {
    try {
      const data = await getInstrumentPrice(this.ticker);

      this.bestSellPrice = data.bestOfferSell?.value || 0;
      this.bestBuyPrice = data.bestOfferBuy?.value || 0;
      this.lotSize = data.lotSize || 1;
      this.price = data.price;
      this.latestPriceDate = {
        value: data.date,
        formatted: data.updatedAt?.formatted || '-'
      };
      this.instrumentMessage = data.message;
      this.isOnWatchlist = data.favorited;

      SignalR.connection.on('MarketData', data => {
        if (data.ticker !== this.ticker) return;

        this.latestPriceDate = data.date;
        this.instrumentStatus = data.status;
        this.price = data.price;
        this.bestBuyPrice = data.bestPriceBuy.value;
        this.bestSellPrice = data.bestPriceSell.value;

        this.setPositionsTotalEarnings(this.price);
      });
    } catch (error) {
      this.error = true;
    }
  }

  private async fetchPositions() {
    try {
      const position = await getInstrumentPosition(this.ticker);
      this.position = position;
    } catch (error) {
      this.error = true;
    }
  }

  private async fetchBalance() {
    try {
      const data = await getBalances();

      this.balanceAvailable = data.account.detail.availableBalance;
    } catch (error) {
      this.error = true;
    }
  }

  handleAcceptRiskTerms() {
    this.canOperate = true;
  }

  private async fetchInstrumentDetailView() {
    try {
      const { about } = await getInstrumentDetailView(this.ticker);
      this.description = about.name.value;
      this.instrumentAbout = about;
    } catch (error) {
      this.instrumentAbout = {};
    }
  }

  private async fetchInstrumentStatus() {
    try {
      const { instrument } = await getInstrumentStatus(this.ticker);

      this.instrument = instrument;

      const invalidRiskProfile = this.instrument.negotiationStatus.buttons.find(
        button => button.action === 'INVALID_RISK_PROFILE'
      );

      this.canOperate = !invalidRiskProfile;
    } catch (error) {
      this.instrumentAbout = {};
    }
  }

  private setPositionsTotalEarnings(price: NumberFormatted) {
    if (!this.position || !this.position.avgPrice?.value) return;

    const totalEarnings =
      (price.value / this.position!.avgPrice!.value) * 100 - 100;
    const totalEarningsFormatted = {
      value: totalEarnings,
      formatted: percentageWithSign(totalEarnings)
    };

    // @ts-ignore
    this.position!.profitPercentage = totalEarningsFormatted;
  }

  get FeatureFlags() {
    return FeatureFlags;
  }

  private onBackClick() {
    if (
      this.$route.query.from ||
      document.referrer.includes('/suitability/profile')
    ) {
      this.$router.push({ name: 'trade' });
    } else {
      this.$router.back();
    }
  }

  onWatchlistClick() {
    trackWatchlistButtonClick(
      this.instrument!.symbol,
      this.description,
      this.instrument?.type?.formatted!
    );
  }

  private refreshInstruments() {
    // eslint-disable-next-line promise/catch-or-return
    Promise.all([this.fetchBalance(), this.fetchPositions()]);
  }
}
</script>

<style lang="less" scoped>
.nav {
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 20;
  * > {
    color: @text-primary !important;
  }
}

.instrument-view__error-container {
  display: flex;
  align-items: center;
  height: 100vh;
}

.instrument-view__error-container__error {
  width: 100%;
}

.instrument-view__deposit-button {
  margin-top: @size-spacing-x800;
  color: @text-negative;
}
</style>
